import { FileCopy, OpenInNew } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { green } from '@mui/material/colors';
import axios from 'axios';
import { z } from 'zod';
import {
  AnchorAction,
  ButtonAction,
  CardLayout,
  FieldFactory,
  GridLayout,
  GroupLayout,
  Resource,
  SidebarLayout,
} from '@/classes';
import GenerateSkusBadge from '@/components/Products/GenerateSkusBadge';
import ArrayBullets from '@/components/Text/ArrayBullets';
import { DECORATION_METHODS, ITEM_CLASSIFICATIONS } from '@/constants';
import {
  productDecorationSchema,
  ProductDetail,
  productImagePayloadSchema,
  productPayloadSchema,
} from '@/types';
import curr from '@/utils/curr';
import { maybeAddQueryParams } from '@/utils/query';

export default function products(): Resource<ProductDetail> {
  return new Resource<ProductDetail>('Products')
    .withSchema(productPayloadSchema)
    .getAvatarUsing((p) => (
      <Avatar variant="square" src={maybeAddQueryParams(p.image, { w: 200 })}>
        {p.name ? p.name[0] : '?'}
      </Avatar>
    ))
    .getSubtitleUsing((p) => (
      <ArrayBullets
        elements={{
          style: p.style_number,
          cost: p.cost ? curr(p.cost) : null,
          vendor: p.vendor.name,
        }}
      />
    ))
    .withDefaultSort('-id')
    .getSingleActionsUsing((values) => [
      new ButtonAction('Duplicate', ({ dialogs, navigate }) => {
        dialogs
          .prompt({
            title: 'Duplicate Product',
            description: 'Please specify a suffix to add to the new SKUs so that they are unique:',
            fields: [FieldFactory.text('suffix')],
            initialValues: {
              suffix: '',
            },
            schema: z.object({
              suffix: z.string(),
            }),
            onSubmit: (v) =>
              axios
                .post<ProductDetail>(`/api/products/${values.id}/duplicate`, v)
                .then(({ data }) => data),
          })
          .then((product) => {
            navigate(`/products/${product.id}`);
          });
      }).withIcon(FileCopy),
      new AnchorAction('View in Customer App', values.portal_url, true).withIcon(OpenInNew),
    ])
    .withFilters([FieldFactory.text('size').withFilterKey('size')])
    .withColumns([
      FieldFactory.text('id').withLabel('#').sortable(),
      FieldFactory.image('image'),
      FieldFactory.number('vendor_inventory_qty'),
      FieldFactory.number('inventory_qty'),
    ])
    .addFieldsAndColumns([
      new SidebarLayout('grid')
        .withFields([
          new CardLayout('', [
            FieldFactory.text('name').withSize('medium').withColumnSpan(8),
            FieldFactory.select('type', {
              product: 'Blank Product',
              decorated: 'Decorated Product',
              service: 'Service',
              decoration: 'Decoration',
            }).filterable(),
            FieldFactory.text('style_number'),
            FieldFactory.text('color'),
            FieldFactory.text('color_code'),
            FieldFactory.belongsTo('vendor', 'vendors')
              .filterable()
              .withColumnSpan(3)
              .with({
                getLink: (value) => `/vendors/${value.id}`,
              }),
            FieldFactory.belongsTo('brand', 'brands').filterable().withColumnSpan(3),
            FieldFactory.belongsTo('customer', 'customers').filterable().withColumnSpan(3),
            FieldFactory.belongsTo('business', 'businesses').filterable().withColumnSpan(3),
          ]),
          new CardLayout('variants')
            .withoutPadding()
            .withoutDefaultLayout()
            .withFields([
              FieldFactory.custom('will_generate_skus', GenerateSkusBadge),
              FieldFactory.hasManyCRUD(
                'variants',
                'productVariants',
                (p) => `/api/products/${p.id}/variants`,
              ).withColumnSpan(12),
            ])
            .setCreatable(false),
          new CardLayout('images', [
            FieldFactory.table(
              'images',
              [
                FieldFactory.image('image').withLabel('Image').with({
                  uploadType: 'product_image',
                  fabric: true,
                }),
                FieldFactory.text('src').withLabel('Image URL'),
                FieldFactory.select('view', ['front', 'back', 'left', 'right', 'top', 'bottom']),
                FieldFactory.boolean('is_blank'),
                FieldFactory.number('sort_order').withLabel('Sort Order'),
              ],
              productImagePayloadSchema,
            ),
          ])
            .withoutDefaultLayout()
            .withoutPadding(),
          new CardLayout('decorations', [
            FieldFactory.table(
              'decorations',
              [
                new GridLayout('', [
                  FieldFactory.text('decoration'),
                  FieldFactory.text('location'),
                  FieldFactory.select('decoration_unit', ['colors', 'stitches', 'other']),
                  FieldFactory.number('decoration_units_max'),
                  FieldFactory.curr('run_price'),
                  FieldFactory.curr('run_cost'),
                ]),
                new CardLayout('Charges', [
                  FieldFactory.list('charges', [
                    FieldFactory.select('type', ['Setup', 'Run', 'Order']),
                    FieldFactory.text('name'),
                    FieldFactory.select('decoration_unit', ['colors', 'stitches', 'other']),
                    FieldFactory.curr('price'),
                    FieldFactory.curr('cost'),
                    FieldFactory.number('min_units'),
                    FieldFactory.number('decoration_units_min'),
                    FieldFactory.boolean('per_color', 'Per Color'),
                  ]),
                ]).withoutDefaultLayout(),
              ],
              productDecorationSchema,
            ).with({
              modalWidth: 'xl',
            }),
          ])
            .withoutDefaultLayout()
            .withoutPadding(),
        ])
        .withSidebarFields([
          new CardLayout('', [
            FieldFactory.select('gender', [
              'Mens',
              'Womens',
              'Boys',
              'Girls',
              'Adult',
              'Youth',
              'Unisex',
            ])
              .withColumnSpan(12)
              .with({
                includeBlank: true,
              }),
            FieldFactory.select('classification', ITEM_CLASSIFICATIONS)
              .withLabel('Tax Classification')
              .withColumnSpan(12),
            FieldFactory.select('preferred_decoration', DECORATION_METHODS)
              .withLabel('Default Decoration Method')
              .withColumnSpan(12),
            FieldFactory.hasMany('tags', 'productTags')
              .with({ createsOptions: true })
              .withColumnSpan(12),
            FieldFactory.hasMany('collections', 'productCollections')
              .withColumnSpan(12)
              .withHelp('This will highlight this style on the Shop page.'),
            FieldFactory.boolean('is_quick_ship', 'Quick Ship').withColumnSpan(12),
            new GroupLayout('Scores', [
              FieldFactory.rating('appeal_score', 'Market Appeal Score'),
              FieldFactory.rating('quality_score', 'Quality Score'),
              FieldFactory.rating('eco_score', 'Eco-Friendly Score').with({
                hexColor: green[500],
              }),
            ]).withColumnSpan(12),
            new GroupLayout('Default Line Item Behavior', [
              FieldFactory.boolean('default_is_purchasable', 'Purchasable'),
              FieldFactory.boolean('default_can_drop_ship', 'Can Drop Ship'),
              FieldFactory.boolean('default_can_apply_designs', 'Can Apply Designs'),
              FieldFactory.boolean('default_is_shippable', 'Shippable'),
              FieldFactory.boolean('default_show_on_invoice', 'Show on Invoice'),
              FieldFactory.boolean('exclude_from_sourcing', 'Exclude from Sourcing'),
            ]).withColumnSpan(12),
          ]),
        ]),
    ]);
}
